import { useTranslation } from 'react-i18next'

import { performRentalPortalListingsMassAction } from '../../../../../api'
import { EMPTY_LIST } from '../../../../../constants/common'
import { QA_NAME_CONFIRMATION } from '../../../../../constants/qa'
import {
  useMassActions,
  useNotification,
  usePerformMassAction,
} from '../../../../../hooks'
import { ConfirmationModal } from '../../../../Confirmation'
import {
  getListingsMassActionNotificationTitle,
  getListingsMassActions,
  LISTING_ACTION_ARCHIVE_MASS,
  LISTING_MASS_ACTION_NAMES,
} from '../../../../RentalPortalListing'

import {
  getListingsMassActionDescription,
  getListingsMassActionEligibleItems,
  getListingsMassActionItems,
} from './useRentalPortalListingsMassActions.utils'

import ArchiveIcon from '../../../../../icons/delete.svg'

export function useRentalPortalListingsMassActions({
  excludeIds,
  filterValues,
  items,
  pagination,
  selectAllPages,
  selected,
  setModal,
  onCleanSelection,
  onClose,
  onReload,
}) {
  const { t } = useTranslation()

  const { handleAddErrorNotification } = useNotification()

  const {
    getAsyncTaskId,
    handleAddAsyncMassActionNotification,
    handleAddFinishMassActionNotification,
  } = useMassActions({
    getNotificationTitle: getListingsMassActionNotificationTitle,
    onSuccessHandler: onReload,
  })

  const massActionItems = getListingsMassActionItems({
    excludeIds,
    items,
    selectAllPages,
    selected,
  })

  const eligibleItems = getListingsMassActionEligibleItems(massActionItems)

  const massActions = getListingsMassActions({
    eligibleCount: eligibleItems.length,
    selectedSize: massActionItems.length,
    t,
  })

  function handleErrorAction() {
    handleAddErrorNotification('RentalPortal.ListingMassActionErrorKey')
  }

  const performerProps = {
    filterValues,
    getAsyncTaskId,
    massActionNames: LISTING_MASS_ACTION_NAMES,
    pagination,
    performApi: performRentalPortalListingsMassAction,
    onAddAsyncMassActionNotification: handleAddAsyncMassActionNotification,
    onAddFinishMassActionNotification: handleAddFinishMassActionNotification,
    onCleanSelection,
    onCloseModal: onClose,
    onErrorAction: handleErrorAction,
    onReload,
  }

  const { massAction: performSelectionMassAction } = usePerformMassAction({
    ...performerProps,
    excludeIds,
    selectAllPages,
    selectedIds: eligibleItems.map(listing => listing.id),
  })

  const { massAction: performRowMassAction } = usePerformMassAction({
    ...performerProps,
    excludeIds: EMPTY_LIST,
    selectAllPages: false,
    selectedIds: EMPTY_LIST,
  })

  function massAction({ action, ids }) {
    return ids?.length > 0
      ? performRowMassAction({ action, ids })
      : performSelectionMassAction({ action })
  }

  function showMassConfirmModal({ action, submitIcon, submitText }) {
    function handleSubmit() {
      return massAction({ action })
    }

    setModal(
      <ConfirmationModal
        actionButtonsProps={{
          submitProps: { beforeIconProps: { Icon: submitIcon } },
          submitText,
        }}
        description={getListingsMassActionDescription({
          eligibleItems,
          selectedCount: massActionItems.length,
          t,
        })}
        qaName={QA_NAME_CONFIRMATION}
        onCancel={onClose}
        onSubmit={handleSubmit}
      />
    )
  }

  function setMassAction(action) {
    if (eligibleItems.length === 0) {
      return null
    }

    switch (action) {
      case LISTING_ACTION_ARCHIVE_MASS:
        showMassConfirmModal({
          action,
          submitIcon: ArchiveIcon,
          submitText: t('RentalPortal.ListingArchiveBtnKey'),
        })
        break
      default:
        return null
    }
  }

  return { massAction, massActions, setMassAction }
}
